import {
  Box,
  InputAdornment,
  TextField as MuiTextField,
  Typography,
  styled,
} from "@mui/material";
import FilterIcon from "../../../assets/filter_icon.svg";
import PdfIcon from "../../../assets/pdf_icon.svg";
import XlsIcon from "../../../assets/xls_icon.svg";
import MainLayout from "../../../layouts/main/main_layout";
import SchemeLayout from "../../../layouts/schemes/scheme_layout";
import FilterEnquiries from "../components/filter_enquiries/filter_enquiries";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DataGridEnquiries from "../components/data_grid/data_grid_enquiries";
import {
  downloadEnquiries,
  getEnquiredSchemes,
} from "../../../services/schemes/schemes";
import { useQuery } from "react-query";
import { getMasterData } from "../../../services/master/master_service";
import { downloadFromBlob } from "../../../utils/utils";
import Loading from "../../../components/loading/loading";
import { RECORDS_EXCEEDS_ALERT_MSG } from "../../../constants";
import { useFeedback } from "../../../providers/feedback/feeedback";
import DownloadPopup from "../components/download_popup/download_popup";
import { ToolbarButton } from "../../../components/toolbar_button/toolbar_button";
import SortIcon from "../../../assets/sort.svg";
import { SearchRounded } from "@mui/icons-material";
import { greyEnabled, purple } from "../../../theme/colors";
import SortEnquiriesPopup, {
  DEFAULT_ENQUIRIES_SORT_OPTION,
  EnquiriesSortOption,
  enquiriesSortOptionLabel,
  enquiriesSortOptionToParams,
} from "../components/sort_enquiries_popup/sort_enquiries_popup";

// ✅ import zustand store
import { useFilterEnquiriesStore, useActiveFilterCount } from "../../../store/use-scheme-enquiries-filter-store";
import { useSelectedOrgStore } from "../../../store/use-selected-org-store";
import OrgSelector from "../../../components/org_selector/org_selector";
export interface EnquiryLabels {
  label: string;
}

export interface PaginationState {
  page: number;
  rowsPerPage: number;
}

const enquiryLabels: EnquiryLabels[] = [
  { label: "Ref.No" },
  { label: "Full Name" },
  { label: "Contact Number" },
  { label: "Scheme Enquired" },
  { label: "State" },
  { label: "District" },
  { label: "Enquired On" },
  { label: "Notification Status" },
];

const TextField = styled(MuiTextField)(() => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    "& fieldset": { borderColor: "#ABA0AB" },
    "&:hover fieldset": { borderColor: purple },
    "&.Mui-focused fieldset": { borderColor: purple, borderWidth: 2 },
  },
}));

export default function Enquiries() {
  const { showSnackbar } = useFeedback();
  const [searchParams, setUrlParam] = useSearchParams();
  const [page, setPage] = useState(
    searchParams?.has("page") ? parseInt(searchParams.get("page")!) : 1
  );

  const [openDownloadUsersPopup, setOpenDownloadPopup] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState("");
  const [downloadSubmitting, setDownloadSubmitting] = useState(false);
  const [sortOption, setSortOption] = useState<EnquiriesSortOption>(DEFAULT_ENQUIRIES_SORT_OPTION);
  const [sortDialogOpen, setSortDialogOpen] = useState(false);
  const activeFilterCount = useActiveFilterCount();
  const { selectedOrgId } = useSelectedOrgStore();

  // ✅ get all filters from zustand
  const {
    setOpenEnquiriesModal,
    startDate,
    endDate,
    schemeKeyword,
    stateId,
    categoryId,
    ageTo,
    gender,
    districtId,
    ageFrom,
    searchValue,
    setSearchValue,
    governmentType,
  } = useFilterEnquiriesStore();

  const [debouncedSearchValue, setDebouncedSearchValue] = useState(searchValue);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchValue]);

  const isFirstSearchRender = useRef(true);
  useEffect(() => {
    if (isFirstSearchRender.current) {
      isFirstSearchRender.current = false;
      return;
    }
    setPage(1);
  }, [debouncedSearchValue]);

  useEffect(() => {
    setPage(1);
  }, [selectedOrgId, sortOption]);

  type EnquiryResult = { list: any[]; total: number };
  const EMPTY_RESULT: EnquiryResult = { list: [], total: 0 };

  const { data: enquiryList, isLoading, isFetching } = useQuery<EnquiryResult>(
  [
    "enquiry",
    page,
    sortOption,
    debouncedSearchValue,
    startDate,
    endDate,
    schemeKeyword,
    stateId,
    categoryId,
    gender,
    districtId,
    ageFrom,
    ageTo,
    governmentType,
    selectedOrgId,
  ],
  async () => {
    try {
      const { sortBy, sortOrder } = enquiriesSortOptionToParams(sortOption);
      const res = await getEnquiredSchemes({
        type: "user",
        dateFrom: startDate,
        dateTo: endDate,
        page,
        size: 8,
        schemeKeyword,
        state: stateId,
        category: categoryId,
        sortBy,
        sortOrder,
        gender,
        districtId,
        ageFrom,
        ageTo,
        keyword: debouncedSearchValue,
        governmentTypeId: governmentType,
        orgId: selectedOrgId,
      });

      // 🔁 Normalize various possible shapes into { responseList, total }
      const payload = res?.data?.data ?? res?.data ?? res ?? {};
      const responseList =
        payload?.data?.list ??
        payload?.responseList ??
        payload?.list ??
        [];
      const total =
        payload?.data?.total ??
        payload?.total ??
        0;

      return { list:responseList, total } as EnquiryResult;
    } catch (err: any) {
      // ✅ For 400, return empty instead of throwing so UI resets
      if (err?.response?.status === 400) {
        return EMPTY_RESULT;
      }
      // For other errors, rethrow (or return EMPTY_RESULT if you prefer)
      throw err;
    }
  },
  {
    retry: false,
    keepPreviousData: true, // optional: smoother UI while refetching
  }
);

  useEffect(() => {
    const currentPage = searchParams.get("page");
    const newPage = page.toString();
    if (currentPage !== newPage) {
      setUrlParam({ page: newPage }, { replace: true });
    }
  }, [page, setUrlParam]);

  const downloadEnquiriesApiCall = (
    format: string,
    fromPage?: number | null,
    toPage?: number | null
  ) => {
    if ((toPage! + 1 - fromPage!) * 10 <= 1000) {
      const { sortBy, sortOrder } = enquiriesSortOptionToParams(sortOption);
      setDownloadSubmitting(true);
      downloadEnquiries({
        type: "user",
        dateFrom: startDate,
        dateTo: endDate,
        page: fromPage!,
        size: (toPage! + 1 - fromPage!) * 10,
        schemeKeyword: schemeKeyword,
        state: stateId,
        category: categoryId,
        format: downloadFormat,
        sortBy,
        sortOrder,
        gender: gender,
        districtId: districtId,
        ageFrom: ageFrom,
        ageTo: ageTo,
        governmentTypeId: governmentType,
        orgId: selectedOrgId,
      })
        .then((res) => {
          let extension = downloadFormat === "pdf" ? ".pdf" : ".xlsx";
          downloadFromBlob(res, new Date().valueOf() + extension);
          setOpenDownloadPopup(false);
        })
        .catch((e) => {
          showSnackbar(
            e?.response?.data?.error?.message ||
              "Something went wrong while generating the report."
          );
        })
        .finally(() => setDownloadSubmitting(false));
    } else {
      showSnackbar(RECORDS_EXCEEDS_ALERT_MSG);
      setOpenDownloadPopup(false);
    }
  };

  // Distinct key from create_user/modify_user's "masterDataList" (a
  // different getMasterData with a different response shape) - sharing a
  // key across two different endpoints let one page's fetch clobber the
  // cache for the other. See users.tsx for the bug this caused.
  const { data: masterDataList, isLoading: masterDataLoading } = useQuery(["masterSchemesDataList"], async () => {
    let response = await getMasterData();
    return response.data.data;
  });

  return (
    <MainLayout>
      <SchemeLayout setPage={setPage} page={page}>
        <Box display="flex" flexDirection="column" mt={3} height="100%">
          <Box
            display="flex"
            flexDirection="row"
            flexWrap="wrap"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
            width="100%"
            px={3}
            py={2}
          >
            <Box maxWidth={{ xs: "100%", sm: 360 }} flex={1} minWidth={{ xs: "100%", sm: 220 }}>
              <TextField
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                size="small"
                fullWidth
                placeholder="Search by name, contact number or ref. no."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRounded sx={{ color: greyEnabled, fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box display="flex" flexDirection="row" flexWrap="wrap" alignItems="center" gap={1.5}>
              <OrgSelector lockToOwnOrg />
              <ToolbarButton
                icon={XlsIcon}
                label="Export XLS"
                onClick={() => {
                  setOpenDownloadPopup(true);
                  setDownloadFormat("excel");
                }}
                loading={downloadSubmitting && downloadFormat === "excel"}
                disabled={downloadSubmitting}
              />
              <ToolbarButton
                icon={PdfIcon}
                label="Export PDF"
                onClick={() => {
                  setOpenDownloadPopup(true);
                  setDownloadFormat("pdf");
                }}
                loading={downloadSubmitting && downloadFormat === "pdf"}
                disabled={downloadSubmitting}
              />
              <ToolbarButton
                icon={SortIcon}
                label={enquiriesSortOptionLabel(sortOption)}
                onClick={() => setSortDialogOpen(true)}
              />
              <ToolbarButton
                icon={FilterIcon}
                label="Filters"
                onClick={() => setOpenEnquiriesModal(true)}
                badgeCount={activeFilterCount}
              />
            </Box>
          </Box>

          {isLoading || isFetching ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <Loading />
            </Box>
          ) : (
            <DataGridEnquiries
              labels={enquiryLabels}
              data={enquiryList?.list || []}
              count={enquiryList?.total || 0}
              page={page}
              setPage={setPage}
            />
          )}

          <SortEnquiriesPopup
            open={sortDialogOpen}
            onClose={() => setSortDialogOpen(false)}
            title="Sort Enquiries"
            sortOption={sortOption}
            setSortOption={setSortOption}
          />

          <FilterEnquiries
            govermentList={masterDataList?.schemeGovernment || []}
            states={masterDataList?.state || []}
            category={masterDataList?.schemeCategory || []}
            genderList={masterDataList?.gender}
            loading={masterDataLoading}
          />
        </Box>

        {openDownloadUsersPopup && (
          <DownloadPopup
            onDialogClose={() => setOpenDownloadPopup(false)}
            open={openDownloadUsersPopup}
            format={downloadFormat === "pdf" ? "pdf" : "excel"}
            key={1}
            submitting={downloadSubmitting}
            onSubmit={(fromPage: number | null, toPage: number | null) => {
              downloadEnquiriesApiCall(downloadFormat, fromPage, toPage);
            }}
          />
        )}
      </SchemeLayout>
    </MainLayout>
  );
}
